import { LitElement, nothing } from 'lit';
import { html, html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { property, query } from 'lit/decorators.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-process-step.styles';
import { SDS_HEADING_LEVELS } from '../types';
import { SDSProcess } from '../sds-process/sds-process';

type HeadingLevels = Exclude<SDS_HEADING_LEVELS, 'h1'>;

export interface SDSProcessStepProps {
	heading?: string;
	timestamp?: string;
}

@guardedCustomElement('sds-process-step')
export class SDSProcessStep extends LitElement {
	static override styles = [styles];
	public readonly internals: ElementInternals;

	@property({ type: String, reflect: true }) heading?: SDSProcessStepProps['heading'];
	@property({ type: String, reflect: true }) timestamp?: SDSProcessStepProps['timestamp'];
	@query('slot[name="marker"]') private markerSlot!: HTMLSlotElement;
	@query('.sds-process-step') private step!: HTMLDivElement;

	private _headingLevel: HeadingLevels = 'h2';

	constructor() {
		super();
		this.internals = this.attachInternals();
		this.internals.role = 'listitem';
	}

	override connectedCallback() {
		super.connectedCallback();
		const parent: SDSProcess | null = this.closest('sds-process');
		if (parent && parent.headingLevel) this._headingLevel = parent.headingLevel;
	}

	override firstUpdated() {
		this._handleMarkerSlotChange();
	}

	private readonly _handleMarkerSlotChange = () => {
		if (!this.markerSlot || !this.step) return;
		const isEmpty = this.markerSlot.assignedNodes({ flatten: true }).length === 0;
		this.step.classList.toggle('sds-process-step--sub-step', isEmpty);
	};

	get safeHeadingLevel(): HeadingLevels {
		const allowed: HeadingLevels[] = ['h2', 'h3', 'h4', 'h5', 'h6'];
		return allowed.includes(this._headingLevel as HeadingLevels)
			? this._headingLevel as HeadingLevels
			: 'h2';
	}

	override render() {
		return html`
			<div class="sds-process-step">
				<div class="sds-process-step__item">
					<div class="sds-process-step__marker" aria-hidden="true">
						<slot name="marker" @slotchange=${this._handleMarkerSlotChange}></slot>
					</div>
					<div class="sds-process-step__content">
						${this.heading ? staticHtml`<${unsafeStatic(this.safeHeadingLevel)} class="sds-process-step__heading">${this.heading}</${unsafeStatic(this.safeHeadingLevel)}>` : nothing}
						${this.timestamp ? html`<div class="sds-process-step__timestamp">${this.timestamp}</div>` : nothing}
						<div class="sds-process-step__slot-content"><slot></slot></div>
					</div>
				</div>
				<span class="sds-process-step__line"></span>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-process-step': SDSProcessStep;
	}
}
